import { Box, Typography } from "@mui/material";
import { useState } from "react";

const COLLAPSED_MAX_HEIGHT = 340;
const COLLAPSE_AFTER = 3;
const TIMELINE_GREEN = "#2E7D32";

export type DigitalCostEstimateQueryRecord = {
  id: string;
  message?: string | null;
  commentedTime?: string;
  createdAt?: string;
  created_at?: string;
  userInfo?: {
    id?: string;
    name?: string;
  } | null;
  userDetail?: {
    name?: string | null;
  } | null;
  user?: {
    firstName?: string;
    lastName?: string;
  } | null;
};

type DigitalCostEstimateQueriesProps = {
  records?: DigitalCostEstimateQueryRecord[] | null;
  title?: string;
  hideTitle?: boolean;
};

const formatDateTime = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  const hours24 = date.getHours();
  const hours12 = hours24 % 12 || 12;
  const meridiem = hours24 >= 12 ? "PM" : "AM";
  return `${pad(date.getDate())}/${pad(
    date.getMonth() + 1
  )}/${date.getFullYear()}, ${pad(hours12)}:${pad(date.getMinutes())}:${pad(
    date.getSeconds()
  )} ${meridiem}`;
};

const getCommentedTime = (record: DigitalCostEstimateQueryRecord) =>
  record.commentedTime ?? record.createdAt ?? record.created_at;

const formatActor = (record: DigitalCostEstimateQueryRecord) => {
  return (
    record.userDetail?.name ||
    record.userInfo?.name ||
    [record.user?.firstName, record.user?.lastName].filter(Boolean).join(" ")
  );
};

const DigitalCostEstimateQueries = ({
  records,
  title = "Digital Cost Estimate Queries",
  hideTitle = false,
}: DigitalCostEstimateQueriesProps) => {
  const [expanded, setExpanded] = useState(false);

  const history: DigitalCostEstimateQueryRecord[] = (
    Array.isArray(records) ? records.filter((record) => record?.message) : []
  ).sort(
    (a, b) =>
      new Date(getCommentedTime(b) ?? 0).getTime() -
      new Date(getCommentedTime(a) ?? 0).getTime()
  );

  if (history.length === 0) return null;

  const collapsible = history.length > COLLAPSE_AFTER;
  const collapsed = collapsible && !expanded;

  return (
    <Box
      sx={{
        margin: "20px 0",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: "primary.main",
        borderRadius: "15px",
        padding: "20px 25px",
        backgroundColor: "#fff",
        maxWidth: { xs: "100%", lg: "60%" },
      }}
    >
      {!hideTitle && (
        <Typography
          component="h3"
          sx={{
            fontFamily: "Poppins",
            fontSize: "22px",
            fontWeight: "500",
            margin: 0,
            marginBottom: "20px",
            paddingBottom: "10px",
            color: "primary.main",
            borderBottomWidth: "1px",
            borderBottomStyle: "solid",
            borderBottomColor: "primary.main",
          }}
        >
          {title}
        </Typography>
      )}

      <Box
        sx={{
          position: "relative",
          maxHeight: collapsed ? `${COLLAPSED_MAX_HEIGHT}px` : "none",
          overflow: "hidden",
        }}
      >
        {history.map((record, index) => {
          const isLast = index === history.length - 1;
          const actor = formatActor(record);
          const commentedTime = formatDateTime(getCommentedTime(record));
          return (
            <Box key={record.id} sx={{ display: "flex", gap: "14px" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    backgroundColor: TIMELINE_GREEN,
                    flexShrink: 0,
                    marginTop: "6px",
                  }}
                />
                {!isLast && (
                  <Box
                    sx={{
                      width: "2px",
                      flexGrow: 1,
                      backgroundColor: "custom.greyish.1",
                    }}
                  />
                )}
              </Box>

              <Box sx={{ paddingBottom: isLast ? 0 : "22px" }}>
                {actor && (
                  <Typography
                    sx={{
                      fontFamily: "Poppins",
                      fontWeight: "600",
                      fontSize: "17px",
                      color: TIMELINE_GREEN,
                    }}
                  >
                    {actor}
                  </Typography>
                )}

                {commentedTime && (
                  <Typography
                    sx={{
                      fontFamily: "Poppins",
                      fontSize: "14px",
                      color: "custom.greyish.2",
                      marginTop: "3px",
                    }}
                  >
                    {commentedTime}
                  </Typography>
                )}

                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "17px",
                    fontWeight: "400",
                    marginTop: "6px",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {record.message}
                </Typography>
              </Box>
            </Box>
          );
        })}

        {collapsed && (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: "90px",
              background:
                "linear-gradient(180deg, rgba(255,255,255,0) 0%, #fff 90%)",
              pointerEvents: "none",
            }}
          />
        )}
      </Box>

      {collapsible && (
        <Typography
          onClick={() => setExpanded((prev) => !prev)}
          sx={{
            fontFamily: "Poppins",
            fontWeight: "500",
            fontSize: "14px",
            color: "primary.main",
            textAlign: "center",
            cursor: "pointer",
            marginTop: "6px",
            userSelect: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {expanded ? "View Less ▲" : "View More ▼"}
        </Typography>
      )}
    </Box>
  );
};

export default DigitalCostEstimateQueries;
